import React, { useState, Fragment, useMemo } from "react";
import { useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { RootState } from "../../store/store";
import {
  Card,
  CardMedia,
  CardActions,
  IconButton,
  Typography,
  CardActionArea,
} from "@material-ui/core";
import MoreVertIcon from "@material-ui/icons/MoreVert";

import ImageCardMenu from "./ImageCardMenu";

import DELETE_ASSET from "../../graphql/DELETE_ASSET";
import { Reference, StoreObject, useMutation } from "@apollo/client";

function ImageCard(props: ImageCardProps) {
  const state = useSelector((state: RootState) => state);
  const [settingsAnchor, setSettingsAnchor] = useState<null | HTMLElement>(
    null
  );

  const classes = useStyles();

  // Define the useMutation hook for deleting an asset
  const [deleteAsset, { loading: deleteLoading, error: deleteError }] =
    useMutation(DELETE_ASSET, {
      context: {
        headers: {
          Authorization: `Bearer ${state.current_user.user?.token}`,
          instance: state.current_site.site?.slug,
        },
      },
      // Update the cache after the mutation
      update(cache, { data: { deleteAsset } }) {
        cache.modify({
          fields: {
            assets(existingAssets = [], { readField }) {
              return existingAssets.filter(
                (assetRef: Reference | StoreObject | undefined) =>
                  readField("filePath", assetRef) !== deleteAsset.filePath
              );
            },
          },
        });
      },
    });

  const handleSettingsOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSettingsAnchor(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsAnchor(null);
  };

  const deleteImage = (filePath: string) => {
    deleteAsset({
      variables: {
        physical_file_path: filePath,
      },
    })
      .then(() => {
        props.handleDeleteAsset(filePath);
      })
      .catch((error) => {
        console.error(`Error deleting asset: ${filePath}`, error);
      });
  };

  const name = useMemo(() => {
    return props.imageName.split("/").pop();
  }, [props.imageName]);

  const cardClasses = (): string => {
    if (props.selectedImageUrl && props.selectedImageUrl === props.imageUrl) {
      return classes.card + " " + classes.active;
    }
    return classes.card;
  };

  return (
    <Fragment>
      {/* Render ungrouped image */}
      <CardActionArea>
        <Card
          variant="outlined"
          className={cardClasses()}
          onClick={() =>
            props.setSelectedImage && props.setSelectedImage(props.imageUrl)
          }
        >
          <CardMedia
            onClick={() => props.handleCopyToClipboard(props.imageUrl)}
            className={classes.media}
            style={{
              objectFit: "contain",
              margin: props.imageFilePath.startsWith("assets/pdf/")
                ? 32
                : "0px",
            }}
            // if props.imageFilePath begins with assets/pdf/ then render this https://upload.wikimedia.org/wikipedia/commons/thumb/8/87/PDF_file_icon.svg/267px-PDF_file_icon.svg.png
            image={
              props.imageFilePath.startsWith("assets/pdf/")
                ? "data:image/svg+xml;base64,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"
                : props.imageUrl
            }
            title={name}
          />

          <CardActions className={classes.actions}>
            <Typography
              variant="subtitle1"
              component="h2"
              className={classes.heading}
            >
              {truncate(name as string)}
            </Typography>
            <IconButton onClick={handleSettingsOpen}>
              <MoreVertIcon />
            </IconButton>
          </CardActions>
        </Card>

        <ImageCardMenu
          imagePath={props.imageFilePath}
          anchor={settingsAnchor}
          handleClose={handleSettingsClose}
          handleDelete={() => deleteImage(props.imageFilePath)}
        />
      </CardActionArea>
    </Fragment>
  );
}

const useStyles = makeStyles((theme) => ({
  card: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  active: {
    border: "solid 2px",
    borderColor: theme.palette.primary.main,
  },
  heading: {
    overflow: "hidden",
  },
  media: {
    height: "200px",
    objectFit: "contain",
  },
  actions: {
    display: "flex",
    justifyContent: "space-between",
    height: "60px",
    overflow: "hidden",
    padding: "0 1rem",
  },
}));

interface ImageCardProps {
  imageUrl: string;
  imageFilePath: string;
  imageName: string;
  imageMeta: Metadata; // <-- Change from string to Metadata
  groupId?: string;
  groupedImages?: { [key: string]: AssetItem[] };
  setSelectedImage?: (url: string) => void;
  selectedImageUrl?: string;
  handleDeleteAsset: (filePath: string) => void;
  handleCopyToClipboard: (url: string) => void;
}

interface AssetItem {
  created_at: string;
  id: string;
  metadata: Metadata;
  name: string;
  physical_file_path: string;
  updated_at: string;
  url: string;
}
interface Metadata {
  group_id?: string; // Assuming group_id is optional
}

function truncate(str: string) {
  return str.length > 19 ? str.substring(0, 19) + "..." : str;
}

export default ImageCard;
